import React, { useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Logger } from '@saviynt/common';
import MfeErrorBoundary from '../Component/MfeErrorBoundary';
import useMfeReady from './useMfeReady';

const useIspmMfeLoader = () => {
  const location = useLocation();
  const containerRefs = useRef({ input: null, menu: null });
  const ispmCopilotState = useSelector((state) => state.ispmMfe) || {};
  
  const isIspmActivePage = location.pathname.includes('ECMv6/intelligence/ispm/dashboard');

  
  const mfeReady = useMfeReady('ispm-ui') && isIspmActivePage;

  // Unmount helper using ref pattern to prevent stale closures
  const unmountRef = useRef();
  unmountRef.current = () => {
    try {
      const module = window.mfes?.ispmUi;
      if (module?.unmountHeaderComponents) {
        module.unmountHeaderComponents();
      }
    } catch (error) {
      Logger.error('MFE unmount failed:', error);
    }
  };
  
  const unmountMfe = useCallback(() => unmountRef.current(), []);

  // Mount/unmount based on overlay state changes
  useEffect(() => {
    if (!mfeReady || !isIspmActivePage) {
      unmountMfe();
      return;
    }

    const module = window.mfes?.ispmUi;
    if (!module?.mountHeaderComponents) return;

    try {
      // Always unmount first
      unmountMfe();

      // Mount the appropriate container with null check
      if (ispmCopilotState.isChatOverlayVisible) {
        const menuContainer = containerRefs.current.menu;
        if (menuContainer) {
          module.mountHeaderComponents({ menuContainer });
        }
      } else {
        const inputContainer = containerRefs.current.input;
        if (inputContainer) {
          module.mountHeaderComponents({ inputContainer });
        }
      }
    } catch (error) {
      Logger.error('MFE mount failed:', error);
    }
  }, [mfeReady, ispmCopilotState.isChatOverlayVisible, isIspmActivePage, unmountMfe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unmountMfe();
      containerRefs.current = { input: null, menu: null };
    };
  }, [unmountMfe]);

  const InputContainer = mfeReady && isIspmActivePage && !ispmCopilotState.isChatOverlayVisible && (
    <MfeErrorBoundary>
      <div 
        ref={(el) => { 
          containerRefs.current.input = el; 
        }}
        id="mfe-input-container"
        style={{ 
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      />
    </MfeErrorBoundary>
  );

  const MenuContainer = mfeReady && isIspmActivePage && ispmCopilotState.isChatOverlayVisible && (
    <MfeErrorBoundary>
      <div 
        ref={(el) => { 
          containerRefs.current.menu = el; 
        }}
        id="mfe-menu-container"
      />
    </MfeErrorBoundary>
  );

  return { IspmInputContainer: InputContainer, IspmEngageMenuContainer: MenuContainer };
};

export default useIspmMfeLoader;